import { memo, PropsWithChildren, useMemo } from 'react'
import { CellDef, FormCellWidth } from '../model'
import { styled, Tooltip } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { SingleSheetPropertyValidationErrors } from '../../hooks/formValidation'
import { ErrorTooltip } from '../../tooltip/ErrorTooltip'
import HelpIcon from '../../../../components/icons/HelpIcon'
import { TextCell } from './TextCell'
import { EmailCell } from './EmailCell'
import { IconCell } from './IconCell'
import { NumberCell } from './NumberCell'
import { MultilineTextCell } from './MultilineTextCell'
import { DateCell } from './DateCell'
import { DateTimeCell } from './DateTimeCell'
import { DateTermCell } from './DateTermCell'
import { SelectCell } from './SelectCell'
import { MultiSelectCell } from './MultiSelectCell'
import { AutocompleteCell } from './AutocompleteCell'
import { MultiAutocompleteCell } from './MultiAutocompleteCell'
import { WorkloadCell } from './WorkloadCell'
import { ProjectMemberAutocompleteCell } from './ProjectMemberAutocompleteCell'
import {
  BooleanModifier,
  DateModifier,
  DateTermModifier,
  DateTimeModifier,
  FormModelModifier,
  MultiLineTextModifier,
  MultiReferencedEntityModifier,
  MultiSelectOptionModifier,
  NumberModifier,
  ReferencedEntityModifier,
  ReferencedEntityWithIconModifier,
  SelectOptionModifier,
  TextModifier,
} from '../../model/formModelModifiers'
import { ToggleCell } from './ToggleCell'

export type CellPropsBase = FormCellProps & {
  label: string
  help?: string
  placeholder?: string
  editable?: boolean
  required?: boolean
  edited?: boolean
  validationErrors?: SingleSheetPropertyValidationErrors
}
type FormCellProps = {
  cellWidth: FormCellWidth
}
export const FormCell = styled('div')(({ cellWidth }: FormCellProps) => {
  const width = useMemo(() => {
    switch (cellWidth) {
      case 'full':
        return '100%'
      case 'half':
        return '50%'
    }
  }, [cellWidth])
  return {
    width,
    minHeight: '34px',
    display: 'flex',
  }
})

type CellTitleProps = {
  title: string
  required?: boolean
  help?: string
}
export const CellTitle = ({
  title,
  required = false,
  help,
}: CellTitleProps) => {
  return (
    <CellTitleArea>
      <CellTitleSpan>{title}</CellTitleSpan>
      {required && <RequiredIndicatorSpan>*</RequiredIndicatorSpan>}
      {help && (
        <Tooltip title={help}>
          <HelpIconArea>
            <HelpIcon />
          </HelpIconArea>
        </Tooltip>
      )}
    </CellTitleArea>
  )
}
const CellTitleArea = styled('div')({
  backgroundColor: colorPalette.monotone[1],
  height: '100%',
  width: '120px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  alignItems: 'center',
  padding: '4px',
  gap: '2px',
})
const CellTitleSpan = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '12px',
  lineHeight: 1,
})
const HelpIconArea = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: 'auto',
  cursor: 'help',
})
const RequiredIndicatorSpan = styled('span')({
  color: colorPalette.pink[5],
})
const CellValueAreaDiv = styled('div')(
  ({ errorMessage }: { errorMessage?: string }) => ({
    border: `1px solid ${colorPalette.monotone[2]}`,
    height: '100%',
    width: '100%',
    flex: '1 1 0%',
    display: 'flex',
    padding: '0 4px',
    alignItems: 'center',
    // overflowY: 'scroll',
    backgroundColor: errorMessage ? colorPalette.pink[2] : undefined,
    position: 'relative',
  })
)

export const CellValueArea = ({
  children,
  onClick,
  validationErros,
  edited,
}: PropsWithChildren<{
  onClick?: () => void
  validationErros?: SingleSheetPropertyValidationErrors
  edited?: boolean
}>) => {
  const errorMessage = useMemo(() => {
    if (!validationErros || validationErros.length === 0) {
      return undefined
    }
    return validationErros.join('\n')
  }, [validationErros])
  return (
    <ErrorTooltip errorMessage={errorMessage}>
      <CellValueAreaDiv onClick={onClick} errorMessage={errorMessage}>
        {children}
        {edited && <EditedMark />}
      </CellValueAreaDiv>
    </ErrorTooltip>
  )
}
const EditedMark = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  width: 0,
  height: 0,
  borderStyle: 'solid',
  borderWidth: '0 10px 10px 0',
  borderColor: `transparent ${colorPalette.yellow[2]} transparent transparent`,
})

export const TableRow = styled('div')({
  display: 'flex',
})

type CellFacadeProps<V> = {
  dataUuid: string
  cellDef: CellDef
  edited: boolean
  validationErrors: SingleSheetPropertyValidationErrors
  value: V
  onChange?: FormModelModifier
}
export const CellFacade = memo(
  ({
    dataUuid,
    cellDef,
    edited,
    validationErrors,
    value,
    onChange,
  }: // TODO: Remove any.
  CellFacadeProps<any>) => {
    switch (cellDef.type) {
      case 'NUMBER':
        return (
          <NumberCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as NumberModifier | undefined}
          />
        )
      case 'TEXT':
        return (
          <TextCell
            label={cellDef.label}
            help={cellDef.help}
            placeholder={cellDef.placeholder}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as TextModifier | undefined}
          />
        )
      case 'EMAIL':
        return (
          <EmailCell
            label={cellDef.label}
            help={cellDef.help}
            placeholder={cellDef.placeholder}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as TextModifier | undefined}
          />
        )
      case 'MULTI_LINE_TEXT':
        return (
          <MultilineTextCell
            dataUuid={dataUuid}
            externalId={cellDef.field}
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as MultiLineTextModifier | undefined}
          />
        )
      case 'DATE':
        return (
          <DateCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as DateModifier | undefined}
          />
        )
      case 'DATE_TIME':
        return (
          <DateTimeCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as DateTimeModifier | undefined}
          />
        )
      case 'DATE_TERM':
        return (
          <DateTermCell
            label={cellDef.label}
            help={cellDef.help}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as DateTermModifier | undefined}
          />
        )
      case 'CHECKBOX':
        return (
          <ToggleCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as BooleanModifier | undefined}
          />
        )
      case 'SELECT':
        return (
          <SelectCell
            label={cellDef.label}
            help={cellDef.help}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as SelectOptionModifier | undefined}
            options={cellDef.options}
            displayIcon={cellDef.displayIcon}
            clearable={cellDef.clearable}
            renderOption={cellDef.renderOption}
            renderValue={cellDef.renderValue}
          />
        )
      case 'MULTI_SELECT':
        return (
          <MultiSelectCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as MultiSelectOptionModifier | undefined}
            options={cellDef.options}
          />
        )
      case 'ENTITY_SEARCH':
        return (
          <AutocompleteCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as ReferencedEntityModifier | undefined}
            displayIcon={cellDef.displayIcon}
            search={cellDef.search}
          />
        )
      case 'MULTI_ENTITY_SEARCH':
        return (
          <MultiAutocompleteCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as MultiReferencedEntityModifier | undefined}
            search={cellDef.search}
            displayIcon={cellDef.displayIcon}
            renderOptionRaw={cellDef.renderOptionRaw}
            renderTags={cellDef.renderTags}
          />
        )
      case 'WORKLOAD':
        return (
          <WorkloadCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as NumberModifier | undefined}
          />
        )
      case 'PROJECT_MEMBER':
        return (
          <ProjectMemberAutocompleteCell
            label={cellDef.label}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as ReferencedEntityWithIconModifier | undefined}
          />
        )
      case 'ICON':
        return (
          <IconCell
            label={cellDef.label}
            help={cellDef.help}
            cellWidth={cellDef.width}
            value={value}
            required={cellDef.required}
            editable={cellDef.editable}
            edited={edited}
            validationErrors={validationErrors}
            onChange={onChange as TextModifier | undefined}
            fileFormat={cellDef.fileFormat}
          />
        )
    }
    return <></>
  }
)
